import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Phone, Mail, Clock, Navigation, ExternalLink, Building2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Google Maps — full 3D satellite share URL of EWRF HQ. Tilt / rotate work here.
const MAP_DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=EWRF+Malaysia+4B+Persiaran+Zaaba+Taman+Tun+Dr+Ismail+Kuala+Lumpur';
// Embed (search-based, hybrid/satellite tile). Loads without an API key.
const MAP_EMBED_URL =
  'https://www.google.com/maps?q=EWRF+Malaysia+4B+Persiaran+Zaaba+Taman+Tun+Dr+Ismail+Kuala+Lumpur&t=k&z=18&output=embed';
const OFFICE_ADDRESS_LINE1 = '4B, Persiaran Zaaba, Taman Tun Dr Ismail';
const OFFICE_ADDRESS_LINE2 = '60000 Kuala Lumpur, Wilayah Persekutuan';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Message Sent!",
      description: "We'll respond within 1-2 business days.",
    });
    setFormData({ fullName: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Subscribed!",
      description: "You're now subscribed to our newsletter.",
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-[#FAF8F3] to-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20">Get in Touch</Badge>
          <h2 className="text-4xl md:text-5xl font-serif text-[#0A1628] mb-4">
            Contact <span className="italic text-[#D4AF37]">Us</span>
          </h2>
          <p className="text-gray-600">Have questions or want to get involved? We&apos;d love to hear from you.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#0A1628] mb-2">Our Office</h5>
                    <p className="text-sm text-gray-600">
                      {OFFICE_ADDRESS_LINE1},<br />
                      {OFFICE_ADDRESS_LINE2}
                    </p>
                    <a
                      href={MAP_DIRECTIONS_URL}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-widest text-[#D4AF37] hover:text-[#B8942F] transition-colors"
                      data-testid="contact-get-directions-inline"
                    >
                      <Navigation className="w-3.5 h-3.5" /> Get Directions
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#0A1628] mb-2">Phone</h5>
                    <p className="text-sm text-gray-600">
                      <a href="tel:+60374987140" className="hover:text-[#D4AF37] transition-colors">03-7498 7140</a><br />
                      WhatsApp: <a href="https://wa.me/60126759467" className="hover:text-[#D4AF37] transition-colors">+60 12-675 9467</a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#0A1628] mb-2">Email</h5>
                    <p className="text-sm text-gray-600">
                      General: <a href="mailto:admin@ewrf.org.my" className="hover:text-[#D4AF37] transition-colors">admin@ewrf.org.my</a><br />
                      Media: <a href="mailto:media@ewrf.org.my" className="hover:text-[#D4AF37] transition-colors">media@ewrf.org.my</a><br />
                      Counselling: <a href="mailto:counselling@ewrf.org.my" className="hover:text-[#D4AF37] transition-colors">counselling@ewrf.org.my</a>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-[#0A1628] mb-2">Office Hours</h5>
                    <p className="text-sm text-gray-600">
                      Mon–Fri, 9:00 AM – 5:30 PM<br />
                      Saturday, 9:00 AM – 1:30 PM<br />
                      Sunday, Closed
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0A1628] mb-4">Send us a message</h3>
                <p className="text-sm text-gray-600 mb-6">Fill out the form below and we&apos;ll respond within 1-2 business days.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="border-gray-300"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="border-gray-300"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Select value={formData.subject} onValueChange={(value) => setFormData({ ...formData, subject: value })}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="programmes">Programmes</SelectItem>
                        <SelectItem value="volunteering">Volunteering</SelectItem>
                        <SelectItem value="donations">Donations</SelectItem>
                        <SelectItem value="counselling">Counselling</SelectItem>
                        <SelectItem value="room">Room Booking</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      className="border-gray-300"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#C19A2E] text-white">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="border-[#D4AF37]/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-[#0A1628] mb-2">Stay in the loop</h3>
                <p className="text-sm text-gray-600 mb-4">Get updates on EWRF programmes, events, and impact stories delivered to your inbox.</p>
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input placeholder="Enter your email" type="email" required className="border-gray-300" />
                  <Button type="submit" className="bg-[#D4AF37] hover:bg-[#C19A2E] text-white whitespace-nowrap">
                    Subscribe
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── 3D Satellite Map ── */}
        <div className="mt-12" data-testid="contact-map-wrap">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Find Us</div>
              <h3 className="text-2xl md:text-3xl font-serif text-[#0A1628]">
                Visit our <span className="italic text-[#D4AF37]">headquarters</span>
              </h3>
              <p className="text-sm text-gray-600 mt-1 inline-flex items-start gap-1.5">
                <Building2 className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                {OFFICE_ADDRESS_LINE1}, {OFFICE_ADDRESS_LINE2}
              </p>
            </div>
            <a
              href={MAP_DIRECTIONS_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-get-directions-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8942F] hover:from-[#C19A2E] hover:to-[#A17F26] text-white px-6 py-3 rounded-full text-sm font-bold uppercase tracking-widest shadow-lg hover:shadow-amber-500/40 hover:scale-105 transition-all"
            >
              <Navigation className="w-4 h-4" />
              Get Directions
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white ring-1 ring-[#D4AF37]/30" data-testid="contact-map-frame">
            <div className="aspect-[16/9] md:aspect-[21/9] w-full bg-gray-100">
              <iframe
                title="EWRF Malaysia — Google Maps 3D Satellite"
                src={MAP_EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                data-testid="contact-map-iframe"
              />
            </div>
            {/* Corner badge */}
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-md rounded-full px-3 py-1.5 shadow-md inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[11px] font-bold text-[#0A1628] tracking-wider">3D SATELLITE VIEW</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center italic">
            Tip: click <b>Get Directions</b> above to open the full 3D map — you can then tilt, rotate, and preview the neighbourhood in Google Maps.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;